import styled from '@emotion/styled';
import { formatDistanceToNow } from 'date-fns';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router';

import settings from 'modules/notifications/data/settings';
import StopPropagationClickBase from 'ui/utils/StopPropagationClick';

import { tryParse } from 'utils/date';
import { humanize, bold } from 'utils/global';

import { StyledReference } from './styles';
import { Content as ContentBase, SubContent, Extension } from './UI';

const StopPropagationClick = styled(StopPropagationClickBase)`
  display: inline;
`;

/**
 * @type {React.FC<import('modules/notifications/types').Notification>}
 * @description - Generic Notification template
 * @example
 *  notification = {
 *   id: '8d969b68-38b8-45f8-b1c2-bd9c9e0b1f42',
 *   createdBy: {
 *       displayName: 'Dimitri Duszynski',
 *       id: 3f1ce74c-4dac-4e5f-be71-67b4432de6f3"
 *       type: 'USER'
 *       ...
 *   },
 *   ...
 *   conversation: {
 *     customer: {
 *        displayName: "Alexis Marot",
 *        id: 5e4ef3db-70cb-4152-8843-de92a8b8f551
 *     },
 *     latestMessage : {
 *         visibility: 'PUBLIC',
 *         content: "Nous vous transmettons le message suivant de la part de notre service client.",
 *         ...
 *     }
 *     ...
 *   },
 * };
 * In the settings files (modules/notifications/data/settings.js) for the CONVERSATION_MESSAGE_CREATED event we have the configuration:
 *  link: notification => ({
 *     title: notification.conversation.latestMessage.createdBy.displayName,
 *     path: generatePath(PATH.CHAT.path, { conversationId: notification.conversation.id }),
 *   }),
 * Generated FormattedMessageId will be: notifications.card.CONVERSATION_MESSAGE_CREATED
 * Translation Example: 'You have a new message from {linkReference}'
 */
const Content = notification => {
  const { eventType, createdOn: date, extension } = notification;

  const definition = settings[extension]?.[eventType];

  const { path, title } = definition?.ui?.(notification).linkReference ?? {};
  const { title: notificationEvent = `${extension}.${eventType}` } = definition?.ui?.(notification) ?? {};
  const extensionKey = extension.toLowerCase();

  const flatten = definition?.ui?.(notification).flatten ?? notification;

  const createdOn = React.useMemo(() => formatDistanceToNow(tryParse(date)), [date]);

  return (
    <ContentBase>
      <FormattedMessage
        id={`notifications.card.${notificationEvent}`}
        defaultMessage="New notification from {group}: {event} {linkReference}"
        tagName="div"
        values={{
          b: bold,
          group: humanize(extension),
          event: humanize(eventType),
          ...flatten,
          linkReference: path ? (
            <StopPropagationClick>
              <Link to={path}>{title}</Link>
            </StopPropagationClick>
          ) : (
            <StyledReference>{title}</StyledReference>
          ),
        }}
      />
      <SubContent>
        <Extension>
          <FormattedMessage id={`notifications.navigation.menu.${extensionKey}`} />
        </Extension>
        -
        <div>{createdOn}</div>
      </SubContent>
    </ContentBase>
  );
};

export default Content;
